/**
 * CustomerDataService – fetches customer address data (Auftraggeber + Lieferadressen).
 *
 * Two endpoints depending on user role:
 * - Employee/Admin: GET /eapi-m-e/customerdata/{customerId}
 * - Customer: GET /eapi-m/customerdata (customerId from token)
 *
 * The response contains the billing address (Auftraggeber) and a list of
 * delivery addresses (Lieferadressen) for the customer.
 */

import { ApiClient } from '../../shared/services/api-client';
import { authService } from '../auth/auth.service';
import { eventBus } from '../../infrastructure/event-bus';
import type { CustomerData } from '../../shared/models/customer-data.model';

// ---------------------------------------------------------------------------
// API Client
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_BASE_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

const apiClient = new ApiClient(getApiBaseUrl());

// ---------------------------------------------------------------------------
// CustomerDataService class
// ---------------------------------------------------------------------------

export class CustomerDataService {
  private customerData: CustomerData | null = null;
  private currentCustomerId: string | null = null;
  private loading = false;
  private listeners: Array<(data: CustomerData | null) => void> = [];

  // -- Query ----------------------------------------------------------------

  /** Get the currently loaded customer data. */
  getCustomerData(): CustomerData | null {
    return this.customerData;
  }

  /** Whether the service is currently loading. */
  isLoading(): boolean {
    return this.loading;
  }

  /** The customerId for which data is currently loaded. */
  getCurrentCustomerId(): string | null {
    return this.currentCustomerId;
  }

  // -- Load -----------------------------------------------------------------

  /**
   * Load customer data for the given customerId.
   *
   * - For employees/admins: calls /eapi-m-e/customerdata/{customerId}
   * - For customers: calls /eapi-m/customerdata (ignores customerId param)
   *
   * Caches the result; re-fetches only when customerId changes.
   */
  async loadCustomerData(customerId?: string): Promise<CustomerData | null> {
    const user = authService.getUser();
    if (!user) return null;

    const isEmployee = user.role === 'employee' || user.role === 'admin';
    const effectiveId = isEmployee ? customerId : user.customerId;

    // Skip if already loaded for this customer
    if (effectiveId && effectiveId === this.currentCustomerId && this.customerData) {
      return this.customerData;
    }

    this.loading = true;
    this.notifyChange();

    try {
      let data: CustomerData;

      if (isEmployee && customerId) {
        data = await apiClient.get<CustomerData>(
          `/eapi-m-e/customerdata/${encodeURIComponent(customerId)}`,
        );
      } else {
        data = await apiClient.get<CustomerData>('/eapi-m/customerdata');
      }

      this.customerData = data;
      this.currentCustomerId = effectiveId || null;
      this.notifyChange();
      return data;
    } catch {
      this.customerData = null;
      this.currentCustomerId = effectiveId || null;
      this.notifyChange();
      return null;
    } finally {
      this.loading = false;
    }
  }

  // -- Reset ----------------------------------------------------------------

  /** Reset local state (e.g. on logout or customer change). */
  reset(): void {
    this.customerData = null;
    this.currentCustomerId = null;
    this.loading = false;
    this.notifyChange();
  }

  // -- Subscriptions --------------------------------------------------------

  /** Subscribe to customer data changes. Returns an unsubscribe function. */
  onCustomerDataChanged(callback: (data: CustomerData | null) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  // -- Internal -------------------------------------------------------------

  private notifyChange(): void {
    const data = this.customerData;
    for (const cb of this.listeners) {
      cb(data);
    }
    eventBus.emit('user-changed');
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

/** Global CustomerDataService instance shared across the application. */
export const customerDataService = new CustomerDataService();
